/**
 * Lightweight Zero-Impact Client Web Analytics Tracker
 * Automatically captures visitor events, referrer, UTM campaigns, device type, 
 * and funnel steps, streaming them to MongoDB for Super Admin Analytics.
 * Strictly adheres to visitor privacy preferences and cookie consent.
 */

import { getConsent, hasConsent } from './consentManager';

// Persistent visitor ID (stored in localStorage ONLY when analytics consent is granted)
const getVisitorId = () => {
  if (!hasConsent('analytics')) {
    return null;
  }
  try {
    let vid = localStorage.getItem('musb_visitor_id');
    if (!vid) {
      vid = 'vis_' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
      localStorage.setItem('musb_visitor_id', vid);
    }
    return vid;
  } catch (e) {
    return null;
  }
};

// Session ID (stored in sessionStorage ONLY when analytics consent is granted)
const getSessionId = () => {
  if (!hasConsent('analytics')) {
    return null;
  }
  try {
    let sid = sessionStorage.getItem('musb_session_id');
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
      sessionStorage.setItem('musb_session_id', sid);
    }
    return sid;
  } catch (e) {
    return null;
  }
};

// Entry landing page (persists across current session)
const getLandingPage = (currentPath) => {
  try {
    let lp = sessionStorage.getItem('musb_landing_page');
    if (!lp) {
      lp = currentPath || '/';
      sessionStorage.setItem('musb_landing_page', lp);
    }
    return lp;
  } catch (e) {
    return currentPath || '/';
  }
};

// Device detector
const getDeviceType = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Lightweight Client Geo & Timezone Detector
const getGeoData = () => {
  try {
    if (typeof window === 'undefined') {
      return { country: 'United States', region: 'Florida', city: 'Tampa', is_tampa_bay: true, timezone: 'America/New_York' };
    }

    // 1. Check cached geo in sessionStorage
    const cached = sessionStorage.getItem('musb_geo_cache');
    if (cached) {
      try { return JSON.parse(cached); } catch (e) {}
    }

    // 2. Detect local browser timezone & locale
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const locale = (navigator.language || (navigator.languages && navigator.languages[0]) || 'en-US').toLowerCase();

    let country = 'United States';
    let region = 'Florida';
    let city = 'Tampa';
    let isTampaBay = false;

    // Timezone mapping
    const tzLower = timeZone.toLowerCase();
    if (tzLower.includes('kolkata') || tzLower.includes('calcutta') || locale.includes('en-in') || locale.includes('hi-in')) {
      country = 'India';
      region = 'Asia';
      city = 'New Delhi';
      isTampaBay = false;
    } else if (tzLower.includes('london') || locale.includes('en-gb')) {
      country = 'United Kingdom';
      region = 'England';
      city = 'London';
      isTampaBay = false;
    } else if (tzLower.includes('toronto') || tzLower.includes('vancouver') || tzLower.includes('montreal') || locale.includes('en-ca') || locale.includes('fr-ca')) {
      country = 'Canada';
      region = tzLower.split('/')[1] || 'Ontario';
      city = region.replace('_', ' ');
      isTampaBay = false;
    } else if (tzLower.includes('sydney') || tzLower.includes('melbourne') || locale.includes('en-au')) {
      country = 'Australia';
      region = 'New South Wales';
      city = 'Sydney';
      isTampaBay = false;
    } else if (tzLower.includes('karachi') || locale.includes('pk')) {
      country = 'Pakistan';
      region = 'Sindh';
      city = 'Karachi';
      isTampaBay = false;
    } else if (tzLower.includes('dhaka') || locale.includes('bd')) {
      country = 'Bangladesh';
      region = 'Dhaka';
      city = 'Dhaka';
      isTampaBay = false;
    } else if (tzLower.includes('dubai') || locale.includes('ae')) {
      country = 'United Arab Emirates';
      region = 'Dubai';
      city = 'Dubai';
      isTampaBay = false;
    } else if (tzLower.includes('berlin') || locale.includes('de')) {
      country = 'Germany';
      region = 'Berlin';
      city = 'Berlin';
      isTampaBay = false;
    } else if (tzLower.includes('paris') || locale.includes('fr')) {
      country = 'France';
      region = 'Île-de-France';
      city = 'Paris';
      isTampaBay = false;
    } else if (tzLower.includes('sao_paulo') || locale.includes('pt-br')) {
      country = 'Brazil';
      region = 'São Paulo';
      city = 'São Paulo';
      isTampaBay = false;
    } else if (tzLower.includes('mexico')) {
      country = 'Mexico';
      region = 'CDMX';
      city = 'Mexico City';
      isTampaBay = false;
    } else if (tzLower.includes('tokyo') || locale.includes('ja')) {
      country = 'Japan';
      region = 'Tokyo';
      city = 'Tokyo';
      isTampaBay = false;
    } else if (tzLower.includes('singapore')) {
      country = 'Singapore';
      region = 'Singapore';
      city = 'Singapore';
      isTampaBay = false;
    } else if (tzLower.includes('new_york') || tzLower.includes('eastern')) {
      country = 'United States';
      region = 'Florida';
      city = 'Tampa';
      isTampaBay = true;
    } else if (tzLower.includes('chicago')) {
      country = 'United States';
      region = 'Illinois';
      city = 'Chicago';
      isTampaBay = false;
    } else if (tzLower.includes('los_angeles')) {
      country = 'United States';
      region = 'California';
      city = 'Los Angeles';
      isTampaBay = false;
    } else if (tzLower.includes('denver') || tzLower.includes('phoenix')) {
      country = 'United States';
      region = 'Mountain';
      city = 'Denver';
      isTampaBay = false;
    } else {
      country = 'International';
      region = timeZone.split('/')[0] || 'Global';
      city = (timeZone.split('/')[1] || 'Visitor').replace('_', ' ');
      isTampaBay = false;
    }

    const geoResult = {
      country,
      region,
      city,
      timezone: timeZone,
      locale,
      is_tampa_bay: isTampaBay,
      zip: ''
    };

    sessionStorage.setItem('musb_geo_cache', JSON.stringify(geoResult));

    // Asynchronously enrich with free IP geo ONLY if analytics consent is explicitly granted
    if (typeof fetch !== 'undefined' && hasConsent('analytics')) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = setTimeout(() => controller?.abort(), 1800);
      fetch('https://freeipapi.com/api/json', { signal: controller?.signal })
        .then(r => r.json())
        .then(res => {
          clearTimeout(timeoutId);
          if (res && res.countryName) {
            const cityName = res.cityName || geoResult.city;
            const enriched = {
              country: res.countryName,
              region: res.regionName || geoResult.region,
              city: cityName,
              zip: res.zipCode || '',
              timezone: timeZone,
              locale,
              is_tampa_bay: ['tampa', 'st. petersburg', 'st petersburg', 'clearwater', 'brandon', 'riverview', 'largo'].some(c => cityName.toLowerCase().includes(c))
            };
            sessionStorage.setItem('musb_geo_cache', JSON.stringify(enriched));
          }
        })
        .catch(() => clearTimeout(timeoutId));
    }

    return geoResult;
  } catch (err) {
    return {
      country: 'United States',
      region: 'Florida',
      city: 'Tampa',
      timezone: 'America/New_York',
      is_tampa_bay: true,
      zip: ''
    };
  }
};

// Extract UTM query parameters or referrer in standard Google Analytics Source / Medium format
const getTrafficSource = () => {
  if (typeof window === 'undefined') return { source: 'direct', campaign: 'direct_traffic', medium: '(none)' };
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmMedium = urlParams.get('utm_medium');
  const gclid = urlParams.get('gclid');
  
  if (utmSource) {
    return {
      source: utmSource.toLowerCase(),
      campaign: utmCampaign || 'general_campaign',
      medium: (utmMedium || 'cpc').toLowerCase()
    };
  }

  if (gclid) {
    return {
      source: 'google',
      campaign: utmCampaign || 'google_ads',
      medium: 'cpc'
    };
  }

  const referrer = document.referrer || '';
  if (!referrer) return { source: 'direct', campaign: 'direct_traffic', medium: '(none)' };

  try {
    const refHost = new URL(referrer).hostname.toLowerCase();
    if (!refHost || refHost.includes(window.location.hostname)) {
      return { source: 'direct', campaign: 'direct_traffic', medium: '(none)' };
    }

    if (refHost.includes('google.')) return { source: 'google', campaign: 'seo_organic', medium: 'organic' };
    if (refHost.includes('bing.')) return { source: 'bing', campaign: 'seo_organic', medium: 'organic' };
    if (refHost.includes('yahoo.')) return { source: 'yahoo', campaign: 'seo_organic', medium: 'organic' };
    if (refHost.includes('facebook.') || refHost.includes('fb.')) return { source: 'facebook.com', campaign: 'social_referral', medium: 'referral' };
    if (refHost.includes('instagram.')) return { source: 'instagram.com', campaign: 'social_referral', medium: 'referral' };
    if (refHost.includes('linkedin.')) return { source: 'linkedin.com', campaign: 'social_referral', medium: 'referral' };
    if (refHost.includes('twitter.') || refHost.includes('t.co') || refHost.includes('x.com')) return { source: 'twitter.com', campaign: 'social_referral', medium: 'referral' };

    const cleanHost = refHost.replace(/^www\./, '');
    return { source: cleanHost, campaign: cleanHost, medium: 'referral' };
  } catch (e) {
    return { source: 'referral', campaign: 'external_referral', medium: 'referral' };
  }
};

// Detect funnel step from current route
const getFunnelStep = (path) => {
  if (!path) return 0;
  if (path === '/tests' || path === '/catalog') return 1;
  if (path.startsWith('/tests/') || path.startsWith('/catalog/')) return 2;
  if (path.startsWith('/book') || path.startsWith('/appointment')) return 3;
  if (path.includes('confirmation') || path.includes('success')) return 4;
  return 0;
};

// Internal traffic detector (excludes superadmin, staff, portal, and employee traffic)
const isInternalTraffic = (pathname) => {
  const p = (pathname || '').toLowerCase();
  const internalPrefixes = [
    '/superadmin',
    '/portal',
    '/staff',
    '/admin',
    '/lab-owner',
    '/lab-manager',
    '/technician',
    '/facility',
    '/doctor',
    '/sales'
  ];
  if (internalPrefixes.some(prefix => p.startsWith(prefix))) {
    return true;
  }

  try {
    const userRole = (localStorage.getItem('user_role') || sessionStorage.getItem('user_role') || '').toLowerCase();
    const isStaff = localStorage.getItem('is_staff') === 'true' || localStorage.getItem('is_superuser') === 'true';
    if (['super_admin', 'superadmin', 'admin', 'staff', 'lab_owner', 'lab_manager', 'technician'].includes(userRole) || isStaff) {
      return true;
    }
  } catch (e) {
    // ignore
  }

  return false;
};

// Dispatch event via sendBeacon or non-blocking async fetch
export const trackWebEvent = (eventType, payload = {}) => {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname;

  // Strict separation of internal / administrative traffic from public metrics
  if (isInternalTraffic(currentPath) && !payload.force) {
    return;
  }

  const consent = getConsent();
  const analyticsConsent = hasConsent('analytics');
  const isPageview = eventType === 'page_view' || eventType === 'pageview';

  // If visitor hasn't granted analytics consent, skip all interaction events (CTA clicks, scrolls, etc.)
  if (!analyticsConsent && !isPageview) {
    return;
  }

  const { source, campaign, medium } = getTrafficSource();
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const landingPage = getLandingPage(currentPath);
  const deviceType = getDeviceType();

  const finalSource = payload.source || source;
  const finalMedium = payload.medium || medium;

  const eventData = {
    event_type: isPageview ? 'pageview' : eventType,
    page_path: currentPath,
    path: currentPath,
    landing_page: landingPage,
    session_id: sessionId,
    visitor_id: visitorId,
    consent_status: consent ? consent.status : 'unconsented',
    consent_categories: consent ? {
      necessary: true,
      preferences: Boolean(consent.preferences),
      analytics: Boolean(consent.analytics),
      marketing: Boolean(consent.marketing)
    } : { necessary: true, preferences: false, analytics: false, marketing: false },
    traffic_source: {
      source: finalSource,
      campaign: payload.campaign || campaign,
      medium: finalMedium,
      source_medium: `${finalSource} / ${finalMedium}`
    },
    source: finalSource,
    campaign: payload.campaign || campaign,
    medium: finalMedium,
    device: {
      type: deviceType,
      browser: 'Chrome',
      os: 'Windows'
    },
    device_type: deviceType,
    referrer: document.referrer || '',
    geo: payload.geo || getGeoData(),
    cta: payload.cta || (payload.meta?.cta_label ? { name: payload.meta.cta_label, label: payload.meta.cta_label } : null),
    funnel_step: payload.funnel_step || getFunnelStep(currentPath),
    performance: {
      load_time_ms: Math.round(performance?.now?.() || 0),
      engagement_seconds: payload.engagement_seconds || 0,
      scroll_depth: payload.scroll_depth || 0,
      error_occurred: Boolean(payload.error_occurred || false)
    },
    meta: payload.meta || {},
    timestamp: new Date().toISOString()
  };

  const endpoint = '/api/superadmin/analytics/collect/';
  const jsonStr = JSON.stringify(eventData);

  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([jsonStr], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    } catch (e) {
      // Fallback to fetch
    }
  }

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: jsonStr,
    keepalive: true
  }).catch(() => {
    // Fail silently so user experience is NEVER impacted
  });
};

// Track specific CTA click
export const trackCTAClick = (ctaLabel, target = '') => {
  trackWebEvent('cta_click', {
    cta: { name: ctaLabel, label: ctaLabel },
    meta: {
      cta_label: ctaLabel,
      target
    }
  });
};

// Track funnel progression
export const trackFunnelStep = (stepName, stepNumber) => {
  trackWebEvent('funnel_step', {
    funnel_step: stepNumber,
    meta: {
      step_name: stepName,
      step_number: stepNumber
    }
  });
};

// Dedicated macro & micro conversion tracking helpers
export const trackPhoneCall = (phoneNumber = '') => {
  trackWebEvent('phone_call_click', {
    cta: { name: 'phone_call_click', label: `Phone Call: ${phoneNumber || 'Direct Tel Link'}` },
    meta: { phone_number: phoneNumber }
  });
};

export const trackBookingFunnelStart = (sourceLabel = 'Booking Flow Initiated') => {
  trackWebEvent('booking_funnel_started', {
    funnel_step: 1,
    cta: { name: 'booking_funnel_started', label: sourceLabel },
    meta: { trigger: sourceLabel }
  });
};

export const trackLabOrderUpload = (fileName = '') => {
  trackWebEvent('lab_order_uploaded', {
    cta: { name: 'lab_order_uploaded', label: 'Doctor Lab Order / Requisition Uploaded' },
    meta: { file_name: fileName }
  });
};

export const trackInquirySubmission = (inquiryType = 'General Contact') => {
  trackWebEvent('inquiry_form_submitted', {
    cta: { name: 'inquiry_form_submitted', label: `Inquiry: ${inquiryType}` },
    meta: { inquiry_type: inquiryType }
  });
};

export const trackCompletedBooking = (appointmentId = '', testName = '') => {
  trackWebEvent('mobile_phlebotomy_booking_completed', {
    funnel_step: 4,
    cta: { name: 'mobile_phlebotomy_booking_completed', label: `Completed Booking: ${testName || appointmentId}` },
    meta: { appointment_id: appointmentId, test_name: testName }
  });
};

const cleanAnalyticsText = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Automatic global listener for phone link clicks, booking CTA triggers, and button clicks
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    try {
      // 1. Phone number click-to-call listener
      const telLink = e.target.closest('a[href^="tel:"], button[data-tel]');
      if (telLink) {
        const href = telLink.getAttribute('href') || telLink.getAttribute('data-tel') || '';
        const phone = href.replace('tel:', '').trim();
        trackPhoneCall(phone);
        return;
      }

      // 2. High-intent booking button listener
      const bookBtn = e.target.closest('a[href*="/book"], button.btn-hero-phleb-primary, button.sp-book-btn');
      if (bookBtn) {
        const rawText = bookBtn.textContent || '';
        const btnText = cleanAnalyticsText(rawText);
        if (/book|schedule|draw/i.test(btnText)) {
          trackBookingFunnelStart(btnText || 'Book Mobile Phlebotomy CTA');
          return;
        }
      }

      // 3. General CTA button and interactive link listener
      const ctaBtn = e.target.closest('button, a.btn, a[class*="button"], a[class*="btn"], [role="button"], [data-cta]');
      if (ctaBtn && !ctaBtn.closest('form')) {
        const rawText = ctaBtn.textContent || ctaBtn.getAttribute('aria-label') || ctaBtn.getAttribute('title') || '';
        const btnText = cleanAnalyticsText(rawText);
        const targetHref = ctaBtn.getAttribute('href') || ctaBtn.getAttribute('data-href') || window.location.pathname;
        if (btnText && btnText.length >= 2 && btnText.length <= 60 && !['close', 'x', 'cancel', 'esc', 'toggle theme'].includes(btnText.toLowerCase())) {
          trackCTAClick(btnText, targetHref);
        }
      }
    } catch (err) {
      // Non-blocking telemetry safeguard
    }
  }, true);
}
